import { 
    useBoolean, 
    usePagination, 
    useTabStorage, 
    useToastr, 
    usePaginationSessionStorage 
} from "@laxmimanogna/code-quick-components";
import { createContext, useMemo, useState } from "react";
import { MY_CHARTS_FILTER_OPTIONS, MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP, SELF } from "../constants/constants";
import { successToast } from "../components/successToast";
import dashboardRepository from "../repositories/DashboardRepository";
import myChartRepository from "../repositories/MyChartRepository";
import { MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME } from "../pages/my_charts/constants";

export const MyChartContext = createContext({
    getAllCharts: async () => { },
    getArchivedCharts: async () => { },
    getRebuttedCharts: async () => { },

    allCharts: [],
    archivedCharts: [],
    rebuttedCharts: [],

    filterParams: {},
    handleApplyFilter: () => { },
    isAllChartLoading: false,
    isArchivedChartLoading: false,
    isRebuttedChartLoading: false,
    getFileObjectByBatchId: async () => { },
    isFileLoading: false,
    attachFiles: async () => { },
    isAttachingAdditionalDocs: false,
});


const MyChartProvider = props => {
    const {
        getPaginationSession,
        setPaginationSession
    } = usePaginationSessionStorage();
    const toast = useToastr();
    const tabStorage = useTabStorage();

    const [allCharts, setAllCharts] = useState([])
    const [archivedCharts, setArchivedCharts] = useState([])
    const [rebuttedCharts, setRebuttedCharts] = useState([])
    const [isAllChartLoading, ialState] = useBoolean(false)
    const [isArchivedChartLoading, iarState] = useBoolean(false)
    const [isRebuttedChartLoading, irState] = useBoolean(false)
    const [isFileLoading, iflState] = useBoolean(false)
    const [isAttachingAdditionalDocs, iadState] = useBoolean(false)    

    const initialFilterParams = useMemo(() => ({
        searchText: getPaginationSession(MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME)?.search || '',
        ordering: getPaginationSession(MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME)?.ordering || '',
        chartStatusType: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() ?? MY_CHARTS_FILTER_OPTIONS.indexOf("All")],
        triggerSearch: true
    }), [])

    const [filterParams, setFilterParams] = useState(
        Object.assign({}, initialFilterParams)
    );

    const getFilterParams = () => {
        const queryParams = {};
        const { searchText, ordering, chartStatusType } = filterParams;
        if (searchText) queryParams['search'] = searchText;
        if (ordering) queryParams['ordering'] = ordering;
        if (chartStatusType) queryParams['chart_statuses'] = MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP[chartStatusType]

        return queryParams;
    };

    const handleApplyFilter = selectedFilters => {
        setFilterParams(selectedFilters);
    };

    const getAllCharts = async (paginationParams) => {
        const filterParams = getFilterParams();
        filterParams['user'] = SELF
        ialState.on()
        try {
            let payload = { ...filterParams, ...paginationParams}
            if ('chart_statuses' in payload && !payload.chart_statuses) {
                delete payload.chart_statuses;
            }
            const response = await myChartRepository.getCharts(payload)
            const annotatedResponse = response.results.map(val => ({...val, batch_providers: [...new Set(val.subRows.map(row => (row.providers ?? []).map(chartProv => chartProv.name)).flat().filter(name => !!name))]}))
            setAllCharts(annotatedResponse)
            return {...response, results: annotatedResponse};
        } catch (err) {
            toast.showError({
                description: `Something went wrong`,
            });
        } finally {
            ialState.off()
        }
    }
    const getArchivedCharts = async (paginationParams) => {
        const filterParams = getFilterParams();
        filterParams['status'] = 'RECEIVED'
        filterParams['user'] = SELF
        iarState.on()
        try {
            const response = await myChartRepository.getCharts({ ...filterParams, ...paginationParams })
            const annotatedResponse = response.results.map(val => ({...val, batch_providers: [...new Set(val.subRows.map(row => (row.providers ?? []).map(chartProv => chartProv.name)).flat().filter(name => !!name))]}));
            setArchivedCharts(annotatedResponse)
            return { ...response, results: annotatedResponse, count: response.results.length }
        } catch (err) {
            toast.showError({
                description: `Something went wrong`,
            });
        } finally {
            iarState.off()
        }
    }

    const getRebuttedCharts = async (paginationParams) => {
        const filterParams = getFilterParams();
        filterParams['status'] = 'rebuttal'
        filterParams['user'] = SELF
        irState.on()
        try {
            const response = await myChartRepository.getCharts({ ...filterParams, ...paginationParams })
            const annotatedResponse = response.results.map(val => ({...val, batch_providers: [...new Set(val.subRows.map(row => (row.providers ?? []).map(chartProv => chartProv.name)).flat().filter(name => !!name))]}))
            setRebuttedCharts(annotatedResponse)
            return { ...response, results: annotatedResponse }
        } catch (err) {
            toast.showError({
                description: `Something went wrong`,
            });
        } finally {
            irState.off()
        }
    }

    const getFileObjectByBatchId = async (batchId) => {
        try {
            iflState.on();
            const response = await dashboardRepository.getFileObjectByBatchId(batchId);
            return response;
        } catch (err) {
            toast.showError({
                description: `Something went wrong.`,
            });
        } finally {
            iflState.off();
        }
    }

    const attachFiles = async (files, chartId, callback) => {
        iadState.on();
        let newFiles = files.map(f => ({
            name: f.name,
            size: `${Math.floor(f.size / 1000)}kb`,
            value: 0,
        }));

        const uploadPromises = files.map(async (file, index) => {
            const formData = new FormData();
            formData.append('files', file);

            const uploadProgress = data => {
                newFiles[index] = {
                    ...newFiles[index],
                    value: Math.round((data.loaded / data.total) * 100),
                };
                callback([...newFiles]);
            };

            try {
                const response = await myChartRepository.attachFiles(chartId, formData, uploadProgress);
                if (response.status === 201) {
                    newFiles[index] = {
                        ...newFiles[index],
                        uploaded: true,
                        cancelled: false,
                        value: 100,
                    };
                    callback([...newFiles]);
                }
            } catch (e) {
                newFiles[index] = {
                    ...newFiles[index],
                    uploaded: false,
                    cancelled: true,
                    value: 100,
                };
                callback([...newFiles]);
                toast.showError({
                    description: e.response?.data?.error || e.message || 'Unable to attach file.',
                });
            }
        });

        await Promise.all(uploadPromises);

        const allSucceeded = newFiles.every(f => f.uploaded);
        if (allSucceeded) {
            successToast(toast, {
                header: 'Success!',
                description: 'Files successfully attached',
            });
        } else {
            toast.showError({
                header: 'Error!',
                description: 'Some files failed to attach',
            });
        }
        iadState.off();
        return allSucceeded;
    };

    const getAllCharts1 = usePagination(getAllCharts)
    const getArchivedCharts1 = usePagination(getArchivedCharts)
    const getRebuttedCharts1 = usePagination(getRebuttedCharts)


    const mContext = {
        getAllCharts: getAllCharts1,
        getArchivedCharts: getArchivedCharts1,
        getRebuttedCharts: getRebuttedCharts1,

        allCharts,
        archivedCharts,
        rebuttedCharts,
        filterParams,
        handleApplyFilter,
        isAllChartLoading,
        isArchivedChartLoading,
        isRebuttedChartLoading,
        getFileObjectByBatchId,
        isFileLoading,
        attachFiles,
        isAttachingAdditionalDocs,
    };

    return (
        <MyChartContext.Provider value={mContext}>
            {props.children}
        </MyChartContext.Provider>
    );
};

export default MyChartProvider;
